// ─────────────────────────────────────────────────────────────────────────────
// page_renderer.ts — Page visual rendering
//
// Draws the physical notebook page: sepia parchment, texture, page edges,
// bookmark tabs, density staining, and the focus-zone blur effect.
// No DOM elements — everything is Canvas 2D.
// ─────────────────────────────────────────────────────────────────────────────

import { PAGE_PALETTE } from './drawn_ui';

export interface PageLayout {
  x: number;          // canvas coordinates of the page left edge
  y: number;
  width: number;
  height: number;
  marginLeft: number;
  marginRight: number;
  marginTop: number;
  marginBottom: number;
}

export interface BookmarkTab {
  label: string;
  tier: 'major' | 'minor';
  active: boolean;
  color?: string;
  index: number;      // position in the tab stack
  /** Special tab kinds: undefined = normal section */
  kind?: 'codex' | 'settings' | 'add' | 'adventure';
  /** Section ID (for section tabs). */
  sectionId?: string;
  /** Whether the tab is locked (dimmed, not clickable). */
  locked?: boolean;
  /** Which side of the bar this tab occupies. Default 'left'. */
  align?: 'left' | 'right';
}

export interface FocusZone {
  active: boolean;
  x: number;
  y: number;
  width: number;
  height: number;
  animT: number;      // 0→1 entrance animation
}

export interface PageSidebarTab {
  label: string;
  active: boolean;
  index: number;
  color?: string;
  kind?: 'add' | 'adventure-sub' | 'folder' | 'back' | 'newpage' | 'broken' | 'delete';
  /** Page ID for direct navigation. */
  pageId?: string;
  /** Tree node path (used for folder tabs to track last-visited state). */
  nodePath?: string;
  /** When true, pin this tab to the bottom of the rail instead of
   *  stacking after the previous one. Used by the delete-page tab so
   *  it always sits at the very bottom regardless of how many
   *  sibling tabs are above. */
  bottomPin?: boolean;
}

// ── Parchment constants ───────────────────────────────────────────────────────

const PARCHMENT_BASE  = '#eedcb0';
const PARCHMENT_DARK  = '#dcc890';
const PARCHMENT_AGED  = '#c9a96e';
const INK_SHADOW      = 'rgba(44, 24, 16, 0.08)';
const BOOK_EDGE       = '#8b6340';
const SPINE_COLOR     = '#6b4020';
const DEFAULT_PAGE_COLOR = '#c8a87a'; // light brown default

/** Pastel paper-toned palette for page sidebar tabs — sits gently
 *  against parchment instead of the previous saturated set. Mirrors
 *  the page-colour palette family so the bookmark tabs and any
 *  matching page background read as part of the same world. */
const PAGE_TAB_PALETTE = [
  '#c9a96e', // parchment gold
  '#a0897a', // warm grey
  '#b08060', // terracotta
  '#8aa0a0', // sage
  '#c0a080', // sand
  '#90a070', // moss
  '#a08090', // dusty rose
  '#80a0b0', // sky
  '#aa9a78', // straw
  '#9088a0', // mauve haze
];

// ── Texture generation (procedural — no image files) ──────────────────────

function buildParchmentTexture(w: number, h: number, seed: number): OffscreenCanvas {
  const oc = new OffscreenCanvas(w, h);
  const ctx = oc.getContext('2d')!;

  // Base fill — warm yellowish sepia
  ctx.fillStyle = PARCHMENT_BASE;
  ctx.fillRect(0, 0, w, h);

  // Fibrous horizontal streaks
  const rng = mulberry32(seed);
  ctx.globalAlpha = 0.04;
  for (let i = 0; i < 120; i++) {
    const y = rng() * h;
    const x0 = rng() * w * 0.3;
    const x1 = w - rng() * w * 0.3;
    const shade = rng() > 0.5 ? '#7a4a18' : '#d4a060';
    ctx.strokeStyle = shade;
    ctx.lineWidth = 0.5 + rng();
    ctx.beginPath();
    ctx.moveTo(x0, y);
    ctx.bezierCurveTo(
      x0 + (x1 - x0) * 0.3, y + (rng() - 0.5) * 6,
      x0 + (x1 - x0) * 0.7, y + (rng() - 0.5) * 6,
      x1, y,
    );
    ctx.stroke();
  }

  // Vertical grain
  ctx.globalAlpha = 0.025;
  for (let i = 0; i < 60; i++) {
    const x = rng() * w;
    ctx.strokeStyle = '#7a4a18';
    ctx.lineWidth = 0.4;
    ctx.beginPath();
    ctx.moveTo(x, 0);
    ctx.lineTo(x + (rng() - 0.5) * 4, h);
    ctx.stroke();
  }

  // Aged spots
  ctx.globalAlpha = 0.06;
  for (let i = 0; i < 40; i++) {
    const sx = rng() * w;
    const sy = rng() * h;
    const r = 2 + rng() * 8;
    const grd = ctx.createRadialGradient(sx, sy, 0, sx, sy, r);
    grd.addColorStop(0, '#8b6340');
    grd.addColorStop(1, 'transparent');
    ctx.fillStyle = grd;
    ctx.beginPath();
    ctx.arc(sx, sy, r, 0, Math.PI * 2);
    ctx.fill();
  }

  ctx.globalAlpha = 1;

  // Imperfections layer — tiny scratches and surface irregularities
  ctx.globalAlpha = 0.03;
  for (let i = 0; i < 50; i++) {
    const sx = rng() * w;
    const sy = rng() * h;
    const len = 2 + rng() * 12;
    const angle = rng() * Math.PI;
    ctx.strokeStyle = rng() > 0.5 ? '#5a3a18' : '#d8c090';
    ctx.lineWidth = 0.3 + rng() * 0.4;
    ctx.beginPath();
    ctx.moveTo(sx, sy);
    ctx.lineTo(sx + Math.cos(angle) * len, sy + Math.sin(angle) * len);
    ctx.stroke();
  }
  // A few faint speckles
  ctx.globalAlpha = 0.04;
  for (let i = 0; i < 80; i++) {
    const sx = rng() * w;
    const sy = rng() * h;
    ctx.fillStyle = rng() > 0.6 ? '#7a5a30' : '#c8b080';
    ctx.fillRect(sx, sy, 0.5 + rng(), 0.5 + rng());
  }

  ctx.globalAlpha = 1;
  return oc;
}

// seeded RNG for reproducible textures
function mulberry32(seed: number): () => number {
  let s = seed >>> 0;
  return () => {
    s += 0x6d2b79f5;
    let t = Math.imul(s ^ (s >>> 15), 1 | s);
    t ^= t + Math.imul(t ^ (t >>> 7), 61 | t);
    return ((t ^ (t >>> 14)) >>> 0) / 0xffffffff;
  };
}

// ── Page renderer ─────────────────────────────────────────────────────────────

export class PageRenderer {
  private _ctx: CanvasRenderingContext2D;
  private _texture: OffscreenCanvas | null = null;
  private _textureSeed = 0;
  private _currentPageColor: string | null = null;

  constructor(ctx: CanvasRenderingContext2D) {
    this._ctx = ctx;
  }

  /** Set the active page's color (used for border tinting). */
  setPageColor(color: string | null): void {
    this._currentPageColor = color;
  }

  /** Call when the notebook loads or page changes to pre-bake the texture. */
  setTextureSeed(seed: number): void {
    if (seed !== this._textureSeed) {
      this._textureSeed = seed;
      this._texture = null; // rebuild on next draw
    }
  }

  /**
   * Draw the full page background.
   * Returns the content rect (inside margins) for ink/block rendering.
   */
  drawPage(layout: PageLayout, densityLevel: number): void {
    const ctx = this._ctx;
    const { x, y, width: w, height: h } = layout;

    // Drop shadow
    ctx.save();
    ctx.shadowColor = 'rgba(0,0,0,0.45)';
    ctx.shadowBlur = 24;
    ctx.shadowOffsetX = 6;
    ctx.shadowOffsetY = 8;
    ctx.fillStyle = PARCHMENT_BASE;
    ctx.fillRect(x, y, w, h);
    ctx.restore();

    // Parchment texture
    if (!this._texture) {
      this._texture = buildParchmentTexture(w, h, this._textureSeed);
    }
    ctx.drawImage(this._texture, x, y);

    // Density staining: as the page fills, a subtle darkening creeps from edges
    if (densityLevel > 0.6) {
      const stain = ctx.createRadialGradient(
        x + w * 0.5, y + h * 0.5, w * 0.3,
        x + w * 0.5, y + h * 0.5, w * 0.75,
      );
      const alpha = ((densityLevel - 0.6) / 0.4) * 0.15;
      stain.addColorStop(0, 'transparent');
      stain.addColorStop(1, `rgba(100, 60, 20, ${alpha})`);
      ctx.fillStyle = stain;
      ctx.fillRect(x, y, w, h);
    }

    // Left spine edge — irregular wavy shadow for organic feel
    const spineGrd = ctx.createLinearGradient(x, y, x + 40, y);
    spineGrd.addColorStop(0, 'rgba(44, 24, 8, 0.35)');
    spineGrd.addColorStop(1, 'transparent');
    ctx.fillStyle = spineGrd;
    ctx.beginPath();
    ctx.moveTo(x, y);
    const SPINE_W = 40;
    const WAVE_AMP = 2;
    const WAVE_FREQ = 0.018;
    // Wavy right edge
    for (let py = y; py <= y + h; py += 4) {
      const dx = SPINE_W + Math.sin(py * WAVE_FREQ) * WAVE_AMP
                          + Math.sin(py * WAVE_FREQ * 2.7 + 1.3) * (WAVE_AMP * 0.4);
      ctx.lineTo(x + dx, py);
    }
    ctx.lineTo(x + SPINE_W + WAVE_AMP, y + h);
    ctx.lineTo(x, y + h);
    ctx.closePath();
    ctx.fill();

    // Border
    ctx.save();
    ctx.strokeStyle = BOOK_EDGE;
    ctx.lineWidth = 1.5;
    ctx.globalAlpha = 0.5;
    ctx.strokeRect(x + 0.75, y + 0.75, w - 1.5, h - 1.5);
    ctx.restore();

    // Page color border line (thick vertical stripe at right and bottom edge)
    const pageColor = this._currentPageColor ?? DEFAULT_PAGE_COLOR;
    ctx.save();
    ctx.fillStyle = pageColor;
    ctx.globalAlpha = 0.45;
    // Right edge stripe (where tabs attach)
    ctx.fillRect(x + w - 4, y, 4, h);
    // Bottom edge stripe
    ctx.fillRect(x, y + h - 3, w, 3);
    ctx.restore();

    // Hardcoded ruling + journal margin rule retired — these were the
    // page's default body+margin graphic. They now live in the
    // decorations system (body_justification + margin_decorations) so
    // every page picks its own treatment. Page chrome here keeps only
    // the parchment texture and edge stripes.
  }

  /**
   * Draw corner bookmarks as vertical rectangles with four stacked sigil slots
   * and a looping line pattern. No text labels.
   *
   * Depth semantics: the active bookmark sits atop the book — its full body
   * (all four sigils) is visible above the page edge. Inactive bookmarks are
   * tucked behind the page — only the top portion peeks above the edge,
   * showing just their topmost sigil.
   *
   * Returns hit rects bounded to the visible (above-page) portion of each bookmark.
   */
  drawTabBar(layout: PageLayout, tabs: BookmarkTab[], _tabBarHeight: number): Array<{ index: number; x: number; y: number; w: number; h: number; kind?: string }> {
    const ctx = this._ctx;
    const hitRects: Array<{ index: number; x: number; y: number; w: number; h: number; kind?: string }> = [];

    // Bookmark dimensions — ~50% larger than v1 (was 20×50, now 30×75)
    const bmW  = 30;
    const bmH  = 75;
    const bmR  = 5;
    const sigilSlots = 4;
    const sigilSpacing = bmH / (sigilSlots + 1);   // ≈15px per slot

    // How much of the bookmark sticks up above the page edge.
    // Active = essentially all of it; inactive = only the topmost sigil peeks.
    const activeProtrude   = bmH - 4;                   // 71px of 75px visible
    const inactiveProtrude = sigilSpacing + 10;         // ≈25px (top sigil + padding)

    // Color palettes per kind
    const palette = (kind: string | undefined, active: boolean) => {
      if (kind === 'codex')    return active ? { fill: '#c8a84a', border: '#8a6020', sigil: '#3a2010' } : { fill: '#9a7038', border: '#6a4c20', sigil: '#e8d8a0' };
      if (kind === 'settings') return active ? { fill: '#9a8878', border: '#6a5848', sigil: '#3a2010' } : { fill: '#706050', border: '#504030', sigil: '#d0c0a0' };
      if (kind === 'newPage')  return active ? { fill: '#a0b070', border: '#607030', sigil: '#2a3010' } : { fill: '#708048', border: '#485020', sigil: '#d8e0b0' };
      return active ? { fill: '#b09060', border: '#7a5830', sigil: '#3a2010' } : { fill: '#887040', border: '#584820', sigil: '#c0a878' };
    };

    // Sigil glyphs — drawn geometrically. All four slots share the same glyph;
    // the top one is primary (large/opaque), the rest are subdued.
    const drawSigil = (kind: string | undefined, cx: number, cy: number, r: number, color: string, primary: boolean) => {
      ctx.save();
      ctx.strokeStyle = color;
      ctx.fillStyle   = color;
      ctx.lineWidth   = primary ? 1.2 : 0.6;
      ctx.globalAlpha = primary ? 0.95 : 0.5;

      if (kind === 'codex') {
        if (primary) {
          ctx.beginPath();
          ctx.arc(cx, cy, r, 0, Math.PI * 2);
          ctx.stroke();
          ctx.beginPath();
          ctx.moveTo(cx, cy - r * 0.55);
          ctx.lineTo(cx + r * 0.55, cy);
          ctx.lineTo(cx, cy + r * 0.55);
          ctx.lineTo(cx - r * 0.55, cy);
          ctx.closePath();
          ctx.fill();
        } else {
          ctx.beginPath();
          ctx.arc(cx, cy, r * 0.45, 0, Math.PI * 2);
          ctx.stroke();
        }
      } else if (kind === 'settings') {
        if (primary) {
          ctx.beginPath();
          ctx.arc(cx, cy, r, 0, Math.PI * 2);
          ctx.stroke();
          ctx.beginPath();
          ctx.moveTo(cx - r * 0.7, cy); ctx.lineTo(cx + r * 0.7, cy);
          ctx.moveTo(cx, cy - r * 0.7); ctx.lineTo(cx, cy + r * 0.7);
          ctx.stroke();
        } else {
          ctx.beginPath();
          ctx.arc(cx, cy, r * 0.45, 0, Math.PI * 2);
          ctx.stroke();
        }
      } else if (kind === 'newPage') {
        // Plus sign — quick action for "new page in current folder".
        if (primary) {
          ctx.lineWidth = 1.5;
          ctx.beginPath();
          ctx.moveTo(cx - r * 0.75, cy); ctx.lineTo(cx + r * 0.75, cy);
          ctx.moveTo(cx, cy - r * 0.75); ctx.lineTo(cx, cy + r * 0.75);
          ctx.stroke();
        } else {
          ctx.beginPath();
          ctx.moveTo(cx - r * 0.35, cy); ctx.lineTo(cx + r * 0.35, cy);
          ctx.moveTo(cx, cy - r * 0.35); ctx.lineTo(cx, cy + r * 0.35);
          ctx.stroke();
        }
      } else {
        ctx.beginPath();
        ctx.arc(cx, cy, primary ? r * 0.7 : r * 0.35, 0, Math.PI * 2);
        ctx.fill();
      }
      ctx.restore();
    };

    // Line pattern — a looping decorative line along the bookmark's center axis
    const drawLinePattern = (bx: number, by: number, active: boolean, kind: string | undefined) => {
      const cx = bx + bmW / 2;
      const color = kind === 'codex'
        ? (active ? 'rgba(58,32,16,0.25)' : 'rgba(232,216,160,0.22)')
        : (active ? 'rgba(58,32,16,0.22)' : 'rgba(208,192,160,0.22)');
      ctx.save();
      ctx.strokeStyle = color;
      ctx.lineWidth   = 1.0;
      ctx.beginPath();
      for (let s = 0; s < sigilSlots - 1; s++) {
        const y0 = by + sigilSpacing * (s + 1);
        const y1 = by + sigilSpacing * (s + 2);
        const dir = s % 2 === 0 ? 1 : -1;
        const ctrl = bmW * 0.32 * dir;
        ctx.moveTo(cx, y0);
        ctx.bezierCurveTo(cx + ctrl, y0 + (y1 - y0) * 0.33, cx - ctrl, y0 + (y1 - y0) * 0.66, cx, y1);
      }
      ctx.stroke();
      ctx.restore();
    };

    // Draw a single bookmark at (bx, by). Clipping handles the "behind page"
    // effect — the bookmark's body extends past layout.y, but the clip rect
    // masks any portion inside the page area.
    const drawBookmark = (tab: BookmarkTab, bx: number, by: number) => {
      const active = tab.active;
      const pal    = palette(tab.kind, active);

      ctx.save();

      // Drop shadow hints at the depth — active sits higher and softer,
      // inactive has a sharper tucked shadow pointing downward
      ctx.shadowColor   = 'rgba(0,0,0,0.42)';
      ctx.shadowBlur    = active ? 10 : 5;
      ctx.shadowOffsetX = 0;
      ctx.shadowOffsetY = active ? 3 : 2;

      // Body — rounded top corners, bottom extends past page edge (clipped out)
      ctx.beginPath();
      ctx.moveTo(bx, by + bmH);
      ctx.lineTo(bx, by + bmR);
      ctx.arcTo(bx, by, bx + bmR, by, bmR);
      ctx.lineTo(bx + bmW - bmR, by);
      ctx.arcTo(bx + bmW, by, bx + bmW, by + bmR, bmR);
      ctx.lineTo(bx + bmW, by + bmH);
      ctx.closePath();

      ctx.fillStyle   = pal.fill;
      ctx.globalAlpha = active ? 1.0 : 0.88;
      ctx.fill();

      ctx.shadowColor = 'transparent';
      ctx.shadowBlur  = 0;

      ctx.strokeStyle = pal.border;
      ctx.lineWidth   = 1.0;
      ctx.globalAlpha = active ? 0.75 : 0.55;
      ctx.stroke();

      ctx.restore();

      drawLinePattern(bx, by, active, tab.kind);

      // All four sigils; only ones above the page edge will actually be visible
      // thanks to the clip. Inactive bookmark → only top sigil in frame.
      const cx = bx + bmW / 2;
      const sigilR = bmW * 0.32;
      for (let s = 0; s < sigilSlots; s++) {
        const sy = by + sigilSpacing * (s + 1);
        drawSigil(tab.kind, cx, sy, sigilR, pal.sigil, s === 0);
      }
    };

    // Clip everything we draw here to above the page top edge.
    // This is what gives inactive bookmarks their "tucked behind" look.
    ctx.save();
    ctx.beginPath();
    ctx.rect(-10000, -10000, 20000 + layout.width + 40000, layout.y + 10000);
    ctx.clip();

    // Layout: Settings leftmost, Codex next, then user bookmarks fan toward right.
    // Tabs are consumed in order; `align: 'left'` stacks from page-left edge,
    // `align: 'right'` stacks from page-right edge inward.
    const gap = 6;
    let leftX  = layout.x + 4;
    let rightX = layout.x + layout.width - 4 - bmW;

    for (let i = 0; i < tabs.length; i++) {
      const tab = tabs[i];
      const isRight = tab.align === 'right';
      const bx = isRight ? rightX : leftX;
      const protrude = tab.active ? activeProtrude : inactiveProtrude;
      const by = layout.y - protrude;  // top of bookmark

      drawBookmark(tab, bx, by);

      // Hit rect covers the visible (above-page) portion only
      hitRects.push({
        index: tab.index,
        x: bx,
        y: layout.y - protrude,
        w: bmW,
        h: protrude,
        kind: tab.kind,
      });

      if (isRight) rightX -= bmW + gap;
      else leftX += bmW + gap;
    }

    ctx.restore();

    return hitRects;
  }

  /** @deprecated Use drawTabBar instead */
  drawBookmarks(layout: PageLayout, tabs: BookmarkTab[]): void {
    // Legacy — delegate to tab bar with default height
    this.drawTabBar(layout, tabs, 28);
  }

  /** Draw the focus zone: blur everything outside, dashed border inside. */
  drawFocusZone(layout: PageLayout, zone: FocusZone): void {
    if (!zone.active) { return; }

    const ctx = this._ctx;
    const { x: px, y: py, width: pw, height: ph } = layout;
    const { x: fx, y: fy, width: fw, height: fh, animT: t } = zone;

    // Interpolate the zone in from the page centre
    const easedT = easeOutCubic(t);
    const ix = px + pw / 2 + (fx - (px + pw / 2)) * easedT;
    const iy = py + ph / 2 + (fy - (py + ph / 2)) * easedT;
    const iw = fw * easedT;
    const ih = fh * easedT;

    // Translucent veil over the rest of the page
    ctx.save();
    ctx.fillStyle = `rgba(245, 228, 195, ${0.72 * easedT})`;
    // Cut out the focus zone using clip path
    ctx.beginPath();
    ctx.rect(px, py, pw, ph);
    ctx.rect(ix, iy, iw, ih);
    // @ts-ignore — evenodd rule
    ctx.fill('evenodd');

    // Dotted border around the zone
    ctx.strokeStyle = `rgba(100, 70, 30, ${0.7 * easedT})`;
    ctx.lineWidth = 1.5;
    ctx.setLineDash([6, 4]);
    ctx.lineDashOffset = -(Date.now() % 1000) / 1000 * 10;  // animated crawl
    ctx.strokeRect(ix, iy, iw, ih);

    ctx.restore();
  }

  /** Draw the page-turn drag animation.  progress 0–1 = drag amount. */
  drawPageTurnHint(layout: PageLayout, progress: number, direction: 'forward' | 'back'): void {
    if (progress < 0.02) { return; }
    const { x, y, width: w, height: h } = layout;
    const ctx = this._ctx;
    const curl = progress * w * 0.25;

    ctx.save();
    ctx.globalAlpha = progress * 0.6;
    ctx.fillStyle = PARCHMENT_DARK;
    if (direction === 'forward') {
      ctx.beginPath();
      ctx.moveTo(x + w, y + h);
      ctx.lineTo(x + w - curl * 2, y + h);
      ctx.lineTo(x + w, y + h - curl * 2);
      ctx.closePath();
      ctx.fill();
    } else {
      ctx.beginPath();
      ctx.moveTo(x, y + h);
      ctx.lineTo(x + curl * 2, y + h);
      ctx.lineTo(x, y + h - curl * 2);
      ctx.closePath();
      ctx.fill();
    }
    ctx.restore();
  }

  // ── Book spine visual (left side) ──────────────────────────────────────────

  drawBookSpine(layout: PageLayout): void {
    const ctx = this._ctx;
    const { x, y, height: h } = layout;
    const spineW = 18;

    ctx.save();

    // Spine body
    const spineGrd = ctx.createLinearGradient(x - spineW, y, x, y);
    spineGrd.addColorStop(0, '#3a2010');
    spineGrd.addColorStop(0.3, '#5a3820');
    spineGrd.addColorStop(0.7, '#6b4020');
    spineGrd.addColorStop(1, '#4a2c14');
    ctx.fillStyle = spineGrd;
    ctx.fillRect(x - spineW, y, spineW, h);

    // Spine bands (horizontal decorative ridges)
    ctx.strokeStyle = '#8b6340';
    ctx.lineWidth = 1;
    ctx.globalAlpha = 0.6;
    const bandCount = 5;
    for (let i = 0; i < bandCount; i++) {
      const by = y + (h / (bandCount + 1)) * (i + 1);
      ctx.beginPath();
      ctx.moveTo(x - spineW + 2, by);
      ctx.lineTo(x - 2, by);
      ctx.stroke();
    }

    // Spine highlight (light reflection along the curve)
    ctx.globalAlpha = 0.15;
    const hlGrd = ctx.createLinearGradient(x - spineW, y, x, y);
    hlGrd.addColorStop(0, 'transparent');
    hlGrd.addColorStop(0.4, 'rgba(255,220,160,0.3)');
    hlGrd.addColorStop(0.6, 'transparent');
    ctx.fillStyle = hlGrd;
    ctx.fillRect(x - spineW, y, spineW, h);

    // Joint groove (dark line where spine meets page)
    ctx.globalAlpha = 0.5;
    ctx.strokeStyle = '#2a1408';
    ctx.lineWidth = 1.5;
    ctx.beginPath();
    ctx.moveTo(x, y);
    ctx.lineTo(x, y + h);
    ctx.stroke();

    ctx.restore();
  }

  // ── Stacked page edges (right side, suggesting thickness of a book) ────────

  drawStackedEdges(layout: PageLayout, totalPages: number, currentIndex: number): void {
    const ctx = this._ctx;
    const { x, y, width: w, height: h } = layout;
    const maxLines = Math.min(totalPages - 1, 8);
    if (maxLines < 1) { return; }

    ctx.save();
    ctx.globalAlpha = 0.25;

    // Pages after current (right side — stacked under)
    const afterCount = Math.min(totalPages - 1 - currentIndex, 4);
    for (let i = 1; i <= afterCount; i++) {
      const offset = i * 2;
      ctx.strokeStyle = BOOK_EDGE;
      ctx.lineWidth = 0.5;
      ctx.beginPath();
      ctx.moveTo(x + w + offset, y + i);
      ctx.lineTo(x + w + offset, y + h - i);
      ctx.stroke();
    }

    // Pages before current (bottom edge — stacked under)
    const beforeCount = Math.min(currentIndex, 4);
    for (let i = 1; i <= beforeCount; i++) {
      const offset = i * 1.5;
      ctx.strokeStyle = BOOK_EDGE;
      ctx.lineWidth = 0.5;
      ctx.beginPath();
      ctx.moveTo(x + i, y + h + offset);
      ctx.lineTo(x + w - i, y + h + offset);
      ctx.stroke();
    }

    ctx.restore();
  }

  // ── Page sidebar (sticky-marker navigation tabs on right side) ─────────────
  //
  // Layout from top:
  //   • back tab  — thin (backH), warm grey, ← arrow, level-up action
  //   • newpage tab — thin (backH), sage green, + icon
  //   • sibling items — folders then pages, regular height, colored markers
  //
  // Each tab is a sticky-note shape: left edge at the page border, right
  // corners rounded, protruding outward with a drop shadow underneath.
  // Active page tab is slightly wider (more overhang) to indicate focus.

  drawPageSidebar(
    layout: PageLayout,
    tabs: PageSidebarTab[],
  ): Array<{ index: number; x: number; y: number; w: number; h: number; kind?: string; pageId?: string; nodePath?: string }> {
    const ctx = this._ctx;
    const { x: px, y: py, width: pw, height: ph } = layout;

    // ── Geometry constants ───────────────────────────────────────────────────
    // Side tabs are icon-only — no rotated text labels. Folders share a
    // uniform bluegray fill and a folder glyph; page tabs keep their per-page
    // accent color and show a page glyph instead of the page name.
    const tabW        = 22;    // total tab width (protrusion)
    const backH       = 26;    // height of back / newpage utility tabs
    const sibH        = 26;    // height of regular sibling tabs (icon-only — no label needed)
    const cornerR     = 5;     // right-corner radius
    const overlapY    = 4;     // vertical overlap between adjacent sibling tabs
    const utilGap     = 4;     // gap between the two utility tabs
    const utilToSib   = 10;    // gap between utility tabs and sibling list
    const startY      = py + 12; // top of the first utility tab
    const pageEdgeX   = px + pw; // right edge of the page (x = 0 of protrusion)
    const anchorX     = pageEdgeX - 2; // left edge of tab (slight overlap with page edge bar)
    const FOLDER_FILL = '#7a8fa0'; // bluegray — uniform across all folder tabs

    const hitRects: Array<{
      index: number; x: number; y: number; w: number; h: number;
      kind?: string; pageId?: string; nodePath?: string;
    }> = [];

    // ── Helpers ──────────────────────────────────────────────────────────────

    const tabPath = (x: number, y: number, w: number, h: number, r: number) => {
      ctx.beginPath();
      ctx.moveTo(x, y);
      ctx.lineTo(x + w - r, y);
      ctx.arcTo(x + w, y, x + w, y + r, r);
      ctx.lineTo(x + w, y + h - r);
      ctx.arcTo(x + w, y + h, x + w - r, y + h, r);
      ctx.lineTo(x, y + h);
      ctx.closePath();
    };

    const drawTab = (
      tab: PageSidebarTab,
      tx: number, ty: number, tw: number, th: number,
      fill: string, alpha: number,
    ) => {
      ctx.save();

      // Drop shadow — projects to the right, giving a "pasted on" depth feel
      ctx.shadowColor   = 'rgba(0,0,0,0.35)';
      ctx.shadowBlur    = 7;
      ctx.shadowOffsetX = 4;
      ctx.shadowOffsetY = 3;

      tabPath(tx, ty, tw, th, cornerR);
      ctx.fillStyle   = fill;
      ctx.globalAlpha = alpha;
      ctx.fill();

      ctx.shadowColor = 'transparent';
      ctx.shadowBlur  = 0;

      // Subtle border
      tabPath(tx, ty, tw, th, cornerR);
      ctx.strokeStyle = 'rgba(40, 20, 8, 0.22)';
      ctx.lineWidth   = 0.8;
      ctx.globalAlpha = 1;
      ctx.stroke();

      ctx.restore();
    };

    // Vertical text label, bottom-up (the standard way for spine labels)
    const drawVertLabel = (
      text: string, tx: number, ty: number, th: number,
      color: string, alpha: number, bold: boolean,
    ) => {
      ctx.save();
      const maxChars = Math.floor((th - 8) / 5.5);
      const label = text.length > maxChars ? text.slice(0, maxChars - 1) + '\u2026' : text;
      ctx.translate(tx + tabW / 2, ty + th / 2);
      ctx.rotate(-Math.PI / 2);
      ctx.font = `${bold ? 'bold ' : ''}9px serif`;
      ctx.textAlign    = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillStyle    = color;
      ctx.globalAlpha  = alpha;
      ctx.fillText(label, 0, 0);
      ctx.restore();
    };

    // Small icon drawn centered in the tab. The icon is the only label —
    // there is no text label anywhere on the side rail anymore.
    const drawIcon = (
      kind: 'back' | 'folder' | 'plus' | 'page' | 'broken' | 'delete',
      tx: number, ty: number, th: number, color: string,
    ) => {
      const cx = tx + tabW / 2;
      const cy = ty + th / 2;
      ctx.save();
      ctx.strokeStyle = color;
      ctx.fillStyle   = color;
      ctx.lineWidth   = 1.4;
      ctx.globalAlpha = 0.92;
      if (kind === 'back') {
        // ← arrow
        const aw = 7; const ah = 5;
        ctx.beginPath();
        ctx.moveTo(cx - aw * 0.5 + aw, cy);
        ctx.lineTo(cx - aw * 0.5, cy);
        ctx.stroke();
        ctx.beginPath();
        ctx.moveTo(cx - aw * 0.5 + 3, cy - ah * 0.5);
        ctx.lineTo(cx - aw * 0.5, cy);
        ctx.lineTo(cx - aw * 0.5 + 3, cy + ah * 0.5);
        ctx.stroke();
      } else if (kind === 'plus') {
        // + icon
        const arm = 6;
        ctx.beginPath();
        ctx.moveTo(cx - arm, cy); ctx.lineTo(cx + arm, cy);
        ctx.moveTo(cx, cy - arm); ctx.lineTo(cx, cy + arm);
        ctx.stroke();
      } else if (kind === 'folder') {
        // Folder glyph: rounded rectangle with a tab-nub on top-left.
        const fw = tabW * 0.55; const fh = tabW * 0.36;
        const fx = cx - fw / 2; const fy = cy - fh / 2 + 1;
        ctx.lineWidth = 1.2;
        const r2 = 2;
        ctx.beginPath();
        ctx.moveTo(fx + r2, fy);
        ctx.lineTo(fx + fw - r2, fy);
        ctx.arcTo(fx + fw, fy, fx + fw, fy + r2, r2);
        ctx.lineTo(fx + fw, fy + fh - r2);
        ctx.arcTo(fx + fw, fy + fh, fx + fw - r2, fy + fh, r2);
        ctx.lineTo(fx + r2, fy + fh);
        ctx.arcTo(fx, fy + fh, fx, fy + fh - r2, r2);
        ctx.lineTo(fx, fy + r2);
        ctx.arcTo(fx, fy, fx + r2, fy, r2);
        ctx.closePath();
        ctx.stroke();
        // Folder tab nub
        const nubW = fw * 0.42; const nubH = 3.5;
        ctx.beginPath();
        ctx.moveTo(fx, fy);
        ctx.lineTo(fx, fy - nubH + 1);
        ctx.arcTo(fx, fy - nubH, fx + 2, fy - nubH, 2);
        ctx.lineTo(fx + nubW - 2, fy - nubH);
        ctx.arcTo(fx + nubW, fy - nubH, fx + nubW, fy - nubH + 2, 2);
        ctx.lineTo(fx + nubW, fy);
        ctx.stroke();
      } else if (kind === 'page') {
        // Page glyph: rounded sheet with a folded-corner notch top-right
        // and a couple of body lines, evoking a written page without using
        // the actual page title as a label.
        const pw_ = tabW * 0.52; const ph_ = tabW * 0.62;
        const fx = cx - pw_ / 2; const fy = cy - ph_ / 2;
        const r3 = 1.5;
        const fold = 3;
        ctx.lineWidth = 1.1;
        ctx.beginPath();
        ctx.moveTo(fx + r3, fy);
        ctx.lineTo(fx + pw_ - fold, fy);
        ctx.lineTo(fx + pw_, fy + fold);
        ctx.lineTo(fx + pw_, fy + ph_ - r3);
        ctx.arcTo(fx + pw_, fy + ph_, fx + pw_ - r3, fy + ph_, r3);
        ctx.lineTo(fx + r3, fy + ph_);
        ctx.arcTo(fx, fy + ph_, fx, fy + ph_ - r3, r3);
        ctx.lineTo(fx, fy + r3);
        ctx.arcTo(fx, fy, fx + r3, fy, r3);
        ctx.closePath();
        ctx.stroke();
        // Folded corner crease
        ctx.beginPath();
        ctx.moveTo(fx + pw_ - fold, fy);
        ctx.lineTo(fx + pw_ - fold, fy + fold);
        ctx.lineTo(fx + pw_, fy + fold);
        ctx.stroke();
        // Two short body lines
        ctx.globalAlpha = 0.55;
        ctx.lineWidth = 0.9;
        const lineX1 = fx + 2.5;
        const lineX2 = fx + pw_ - 2.5;
        const ly1 = fy + ph_ * 0.55;
        const ly2 = fy + ph_ * 0.78;
        ctx.beginPath();
        ctx.moveTo(lineX1, ly1); ctx.lineTo(lineX2, ly1);
        ctx.moveTo(lineX1, ly2); ctx.lineTo(lineX2 - 4, ly2);
        ctx.stroke();
      } else if (kind === 'broken') {
        // Red cross — load fault indicator. Replaces the page glyph
        // for tabs whose most recent _ensurePageLoaded threw.
        const arm = 5;
        ctx.strokeStyle = '#b32a1a';
        ctx.lineWidth = 1.6;
        ctx.lineCap = 'round';
        ctx.beginPath();
        ctx.moveTo(cx - arm, cy - arm); ctx.lineTo(cx + arm, cy + arm);
        ctx.moveTo(cx + arm, cy - arm); ctx.lineTo(cx - arm, cy + arm);
        ctx.stroke();
      } else if (kind === 'delete') {
        // White-tinted cross over the red tab fill — "remove current
        // page" affordance. Distinct from 'broken' (which is the red
        // cross over a parchment tab to flag a load fault) by virtue
        // of the surrounding tab being red, not parchment.
        const arm = 5;
        ctx.strokeStyle = color;
        ctx.lineWidth = 1.8;
        ctx.lineCap = 'round';
        ctx.beginPath();
        ctx.moveTo(cx - arm, cy - arm); ctx.lineTo(cx + arm, cy + arm);
        ctx.moveTo(cx + arm, cy - arm); ctx.lineTo(cx - arm, cy + arm);
        ctx.stroke();
      }
      ctx.restore();
    };

    // ── Tab color resolution ─────────────────────────────────────────────────
    const tabColor = (tab: PageSidebarTab): string => {
      if (tab.color) return tab.color;
      return PAGE_TAB_PALETTE[tab.index >= 0 ? tab.index % PAGE_TAB_PALETTE.length : 0];
    };

    // ── Layout pass: compute y positions for each tab ────────────────────────
    type Geom = {
      tab: PageSidebarTab;
      tx: number; ty: number; tw: number; th: number;
      fill: string; textAlpha: number; fillAlpha: number;
    };
    const geoms: Geom[] = [];

    // Split into top-stacked tabs and bottom-pinned tabs. Bottom-
    // pinned tabs always render flush to the page's bottom edge
    // regardless of how many siblings stack from the top.
    const topTabs: PageSidebarTab[] = [];
    const bottomTabs: PageSidebarTab[] = [];
    for (const t of tabs) {
      if (t.bottomPin) bottomTabs.push(t);
      else topTabs.push(t);
    }

    const resolveStyle = (tab: PageSidebarTab) => {
      let fill: string;
      let textAlpha: number;
      let fillAlpha: number;
      if (tab.kind === 'back') {
        fill      = '#8a7a6a'; // warm grey
        textAlpha = 0.9;
        fillAlpha = 0.88;
      } else if (tab.kind === 'newpage') {
        fill      = '#6a8a60'; // sage green
        textAlpha = 0.9;
        fillAlpha = 0.88;
      } else if (tab.kind === 'delete') {
        fill      = '#b34a3a'; // muted brick red — distinct, not screaming
        textAlpha = 0.9;
        fillAlpha = 0.88;
      } else if (tab.kind === 'folder') {
        fill      = FOLDER_FILL;
        textAlpha = tab.active ? 1 : 0.78;
        fillAlpha = tab.active ? 1.0 : 0.82;
      } else {
        fill      = tabColor(tab);
        textAlpha = tab.active ? 1 : 0.78;
        fillAlpha = tab.active ? 1.0 : 0.78;
      }
      return { fill, textAlpha, fillAlpha };
    };

    // Top stack — same accumulation as before.
    let curY = startY;
    for (let i = 0; i < topTabs.length; i++) {
      const tab = topTabs[i];
      const isUtil = tab.kind === 'back' || tab.kind === 'newpage';
      const th = isUtil ? backH : sibH;
      const tw = tabW + (tab.active ? 4 : 0);
      const { fill, textAlpha, fillAlpha } = resolveStyle(tab);

      geoms.push({ tab, tx: anchorX, ty: curY, tw, th, fill, textAlpha, fillAlpha });
      hitRects.push({
        index: tab.index, x: anchorX, y: curY, w: tw, h: th,
        kind: tab.kind, pageId: tab.pageId, nodePath: tab.nodePath,
      });

      if (isUtil) {
        curY += th + (i < topTabs.length - 1 && (topTabs[i + 1]?.kind === 'back' || topTabs[i + 1]?.kind === 'newpage') ? utilGap : utilToSib);
      } else {
        curY += th - overlapY;
      }
      if (curY > py + ph - 10) break;
    }

    // Bottom-pinned tabs — stack from page-bottom upward.
    let bottomY = py + ph - 12;
    for (const tab of bottomTabs) {
      const th = backH;
      const tw = tabW + (tab.active ? 4 : 0);
      const { fill, textAlpha, fillAlpha } = resolveStyle(tab);
      const ty = bottomY - th;
      geoms.push({ tab, tx: anchorX, ty, tw, th, fill, textAlpha, fillAlpha });
      hitRects.push({
        index: tab.index, x: anchorX, y: ty, w: tw, h: th,
        kind: tab.kind, pageId: tab.pageId, nodePath: tab.nodePath,
      });
      bottomY = ty - 4;
    }

    // ── Draw pass: inactive first, then active on top ────────────────────────

    // Inactive tabs (icon-only — text labels are gone, the colored chip +
    // glyph carries identity)
    for (const g of geoms) {
      if (g.tab.active) continue;
      drawTab(g.tab, g.tx, g.ty, g.tw, g.th, g.fill, g.fillAlpha);
      if (g.tab.kind === 'back') {
        drawIcon('back', g.tx, g.ty, g.th, '#f0e0c8');
      } else if (g.tab.kind === 'newpage') {
        drawIcon('plus', g.tx, g.ty, g.th, '#d8f0d0');
      } else if (g.tab.kind === 'delete') {
        drawIcon('delete', g.tx, g.ty, g.th, '#f8e8e0');
      } else if (g.tab.kind === 'folder') {
        drawIcon('folder', g.tx, g.ty, g.th, '#f0f4f8');
      } else if (g.tab.kind === 'broken') {
        drawIcon('broken', g.tx, g.ty, g.th, '#b32a1a');
      } else {
        drawIcon('page', g.tx, g.ty, g.th, '#fdf6e3');
      }
    }

    // Active tab — same icons, brighter strokes
    for (const g of geoms) {
      if (!g.tab.active) continue;
      drawTab(g.tab, g.tx, g.ty, g.tw, g.th, g.fill, g.fillAlpha);
      if (g.tab.kind === 'back') {
        drawIcon('back', g.tx, g.ty, g.th, '#fff8f0');
      } else if (g.tab.kind === 'newpage') {
        drawIcon('plus', g.tx, g.ty, g.th, '#f0fff0');
      } else if (g.tab.kind === 'delete') {
        drawIcon('delete', g.tx, g.ty, g.th, '#ffffff');
      } else if (g.tab.kind === 'folder') {
        drawIcon('folder', g.tx, g.ty, g.th, '#ffffff');
      } else if (g.tab.kind === 'broken') {
        drawIcon('broken', g.tx, g.ty, g.th, '#b32a1a');
      } else {
        drawIcon('page', g.tx, g.ty, g.th, '#ffffff');
      }
    }

    // drawVertLabel kept around for any future re-introduction of text labels;
    // the icon-only mode currently never calls it.
    void drawVertLabel;

    return hitRects;
  }

  // ── Page header bar (sticky name + inline color palette at top of page) ────

  drawPageHeader(
    layout: PageLayout,
    title: string,
    color?: string,
    lastUpdated?: string,
    hideSwatches?: boolean,
    /** When the page belongs to a multi-page sheet, passing {index,count}
     *  renders `‹ title n/m ›` arrows on either side of the title text.
     *  Returned `sheetArrows` are canvas-absolute hit rects the caller
     *  should register as subpageArrow UiHitRects. */
    sheet?: { index: number; count: number; blockId: string },
  ): {
    colorSwatches: Array<{ color: string; x: number; y: number; r: number }>;
    titleRect: { x: number; y: number; w: number; h: number };
    sheetArrows: Array<{ blockId: string; dir: -1 | 1; x: number; y: number; w: number; h: number }>;
  } {
    const ctx = this._ctx;
    const { x: px, y: py, width: pw, marginLeft: ml, marginRight: mr } = layout;
    const headerH = 28;
    const headerY = py + 8;
    const contentX = px + ml;
    const contentW = pw - ml - mr;
    const midY = headerY + headerH / 2;

    // Layout constants for the inline color swatch row (right side)
    const swatchR       = 6;
    const swatchSpacing = 16;    // center-to-center
    const numSwatches   = PAGE_PALETTE.length;               // 8
    const swatchRowW    = (numSwatches - 1) * swatchSpacing; // 77px
    const firstSwatchX  = contentX + contentW - 8 - swatchRowW;
    const swatchY       = midY;

    ctx.save();

    // Subtle header strip background
    ctx.fillStyle = color ? (color + '18') : 'rgba(180, 150, 100, 0.06)';
    ctx.fillRect(contentX, headerY, contentW, headerH);

    // Bottom edge line
    ctx.strokeStyle = color ?? 'rgba(140, 110, 70, 0.15)';
    ctx.lineWidth   = 0.5;
    ctx.globalAlpha = 0.35;
    ctx.beginPath();
    ctx.moveTo(contentX, headerY + headerH);
    ctx.lineTo(contentX + contentW, headerY + headerH);
    ctx.stroke();

    // Left-anchored page title. The old layer selector left of the
    // title is gone (retired); per-content modifiers (lock / group)
    // surface at the selection level instead. Astral stays internal.
    const swatchZoneW = swatchRowW + swatchR * 2 + 16;
    const titleMaxW   = contentW - swatchZoneW - 20;
    // The locked decoration rectangle around the title extends to
    // about half the content (line) width — a calm horizontal frame
    // anchored to the left margin.
    const titleFrameW = Math.min(contentW / 2, titleMaxW);
    const titleFramePad = 8;   // inner padding so text breathes inside the frame
    const titleX = contentX + titleFramePad;     // left edge of title text
    ctx.globalAlpha  = 0.65;
    ctx.fillStyle    = '#3a2010';
    ctx.font         = 'italic 11px Georgia, serif';
    ctx.textAlign    = 'left';
    ctx.textBaseline = 'middle';

    // Sheet paginator — when the sheet has >1 page, wrap the title with
    // `‹ title n/m ›` arrows.  Clicks on the arrows are relayed via
    // `sheetArrows` so CanvasApp can register them as subpage-navigation
    // hits.  This promotes the per-block subpage strip to the page header
    // so file-imports feel like "a sheet of pages", not "a paginated block".
    const sheetArrows: Array<{
      blockId: string; dir: -1 | 1; x: number; y: number; w: number; h: number;
    }> = [];
    const titleTextMaxW = Math.max(titleFrameW - titleFramePad * 2 - 30, 60);
    if (sheet && sheet.count > 1) {
      const labelText = `${title}  ${sheet.index + 1}/${sheet.count}`;
      const labelW = Math.min(
        titleTextMaxW - 50 /* reserve for arrows */,
        ctx.measureText(labelText).width + 4,
      );
      const arrowGap = 12;
      // Left-anchored layout: ‹  label  ›
      const leftArrowX = titleX;
      const labelStartX = leftArrowX + arrowGap;
      const rightArrowX = labelStartX + labelW + 4;

      ctx.save();
      ctx.font = 'bold 14px Georgia, serif';
      ctx.textAlign = 'left';
      ctx.textBaseline = 'middle';
      ctx.globalAlpha = sheet.index > 0 ? 0.9 : 0.25;
      ctx.fillStyle = '#3a2010';
      ctx.fillText('‹', leftArrowX, midY);
      ctx.globalAlpha = sheet.index < sheet.count - 1 ? 0.9 : 0.25;
      ctx.fillText('›', rightArrowX, midY);
      ctx.restore();

      ctx.fillText(labelText, labelStartX, midY, labelW);

      const HPAD = 10;
      sheetArrows.push({
        blockId: sheet.blockId, dir: -1,
        x: leftArrowX - HPAD, y: headerY, w: 2 * HPAD, h: headerH,
      });
      sheetArrows.push({
        blockId: sheet.blockId, dir: 1,
        x: rightArrowX - HPAD, y: headerY, w: 2 * HPAD, h: headerH,
      });
    } else {
      ctx.fillText(title, titleX, midY, titleTextMaxW);
    }

    // Faint chrome fields — a hairline rectangle around the title and
    // around the color-swatch row, so the two interactive zones read
    // as discrete fields without competing with content for attention.
    ctx.save();
    ctx.globalAlpha = 0.32;
    ctx.strokeStyle = color ?? 'rgba(58, 32, 16, 0.55)';
    ctx.lineWidth = 0.7;
    const fieldInsetY = 3;
    // Title field — left, spanning the title text area
    ctx.strokeRect(
      contentX,
      headerY + fieldInsetY,
      titleFrameW,
      headerH - fieldInsetY * 2,
    );
    // Swatch field — right, snug around the swatch row
    if (!hideSwatches) {
      const swatchFieldPad = 8;
      const swatchFieldX = firstSwatchX - swatchR - swatchFieldPad;
      const swatchFieldW = swatchRowW + swatchR * 2 + swatchFieldPad * 2;
      ctx.strokeRect(
        swatchFieldX,
        headerY + fieldInsetY,
        swatchFieldW,
        headerH - fieldInsetY * 2,
      );
    }
    ctx.restore();

    // Right corner: inline static color swatch row
    const colorSwatches: Array<{ color: string; x: number; y: number; r: number }> = [];
    if (!hideSwatches) {
      for (let i = 0; i < numSwatches; i++) {
        const c  = PAGE_PALETTE[i];
        const sx = firstSwatchX + i * swatchSpacing;
        const isSelected = c === color;

        ctx.globalAlpha = isSelected ? 0.92 : 0.55;
        ctx.fillStyle   = c;
        ctx.beginPath();
        ctx.arc(sx, swatchY, isSelected ? swatchR + 1 : swatchR, 0, Math.PI * 2);
        ctx.fill();

        colorSwatches.push({ color: c, x: sx, y: swatchY, r: swatchR + 4 });
      }
    }

    ctx.restore();

    // Title hit rect: left-anchored, spanning the frame width
    const titleRect = { x: contentX, y: headerY, w: titleFrameW, h: headerH };

    return { colorSwatches, titleRect, sheetArrows };
  }

  // ── Section header page ────────────────────────────────────────────────────
  // Renders a decorative section title page with a large name line and
  // color selection spots.  The name and color are editable via popup dialog.

  drawSectionHeaderPage(
    layout: PageLayout,
    sectionTitle: string,
    sectionColor: string,
    colorPalette: string[],
    customizable = true,
  ): { nameLineRect: { x: number; y: number; w: number; h: number }; colorSpots: Array<{ color: string; x: number; y: number; r: number }> } {
    const ctx = this._ctx;
    const { x: px, y: py, width: pw, height: ph, marginLeft: ml, marginRight: mr, marginTop: mt } = layout;
    const cx = px + pw / 2;

    // Section title (large, centered, at ~1/3 from top)
    const titleY = py + mt + ph * 0.22;
    ctx.save();
    ctx.fillStyle = sectionColor || '#5a3820';
    ctx.font = 'bold italic 28px Georgia, serif';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.globalAlpha = 0.8;
    ctx.fillText(sectionTitle || 'Untitled Section', cx, titleY);

    // Name underline (hand-drawn feel)
    const lineW = Math.min(pw - ml - mr - 40, 260);
    ctx.strokeStyle = sectionColor || '#8b6340';
    ctx.lineWidth = 1;
    ctx.globalAlpha = 0.35;
    ctx.beginPath();
    ctx.moveTo(cx - lineW / 2, titleY + 22);
    ctx.bezierCurveTo(
      cx - lineW / 4, titleY + 24,
      cx + lineW / 4, titleY + 20,
      cx + lineW / 2, titleY + 22,
    );
    ctx.stroke();

    // Color palette spots (horizontal row below the name)
    const spotY = titleY + 58;
    const spotR = 10;
    const spotGap = 32;
    const spots: Array<{ color: string; x: number; y: number; r: number }> = [];

    if (customizable) {
      const totalSpotW = (colorPalette.length - 1) * spotGap;
      const spotStartX = cx - totalSpotW / 2;

      for (let i = 0; i < colorPalette.length; i++) {
        const sx = spotStartX + i * spotGap;
        const c = colorPalette[i];
        const isCurrent = c === sectionColor;

        ctx.globalAlpha = isCurrent ? 0.9 : 0.55;
        ctx.fillStyle = c;
        ctx.beginPath();
        ctx.arc(sx, spotY, isCurrent ? spotR + 2 : spotR, 0, Math.PI * 2);
        ctx.fill();

        spots.push({ color: c, x: sx, y: spotY, r: spotR });
      }
    }

    // Decorative ornamental line below the title area
    const ornY = customizable ? spotY + spotR + 30 : titleY + 50;
    ctx.globalAlpha = 0.12;
    ctx.strokeStyle = '#5a3820';
    ctx.lineWidth = 0.5;
    ctx.beginPath();
    ctx.moveTo(cx - 60, ornY);
    ctx.lineTo(cx - 10, ornY);
    ctx.moveTo(cx + 10, ornY);
    ctx.lineTo(cx + 60, ornY);
    // Small diamond
    ctx.moveTo(cx - 4, ornY);
    ctx.lineTo(cx, ornY - 4);
    ctx.lineTo(cx + 4, ornY);
    ctx.lineTo(cx, ornY + 4);
    ctx.closePath();
    ctx.stroke();

    ctx.restore();

    const nameLineRect = {
      x: cx - lineW / 2 - 10,
      y: titleY - 20,
      w: lineW + 20,
      h: 50,
    };

    return { nameLineRect, colorSpots: spots };
  }

  // ── Fold frontpage door decoration ──────────────────────────────────────────

  /**
   * Draw The Fold's full-page tavern door.  The entire section-header page
   * IS the door: gothic pointed arch filling the page, title plaque,
   * bookmark emblem with four sigils, grass overgrowth at the base.
   */
  drawFoldDoor(layout: PageLayout, sectionColor: string, title?: string): void {
    const ctx = this._ctx;
    const { x: px, y: py, width: pw, height: ph, marginLeft: ml, marginRight: mr, marginTop: mt, marginBottom: mb } = layout;
    const cx = px + pw / 2;
    const contentX = px + ml;
    const contentW = pw - ml - mr;
    const pageTop = py + mt + 4;
    const pageBottom = py + ph;              // flush to page bottom edge

    // ── Door: 75% of content width, centred ──
    const doorW = contentW * 0.75;
    const doorX = cx - doorW / 2;
    const archR = doorW / 2;
    // Arch circle centre aligns so the circle's bottom tangent meets the door body top
    const archCY = pageTop + archR;
    const bodyTop = archCY + archR;          // straight sides start where circle ends
    const doorBottom = pageBottom;

    const col = sectionColor || '#6b5b3b';

    ctx.save();

    // ── 1. Door shadow ──
    ctx.globalAlpha = 0.05;
    ctx.fillStyle = col;
    ctx.beginPath();
    ctx.moveTo(doorX - 8, bodyTop + 8);
    ctx.lineTo(doorX - 8, doorBottom + 6);
    ctx.lineTo(doorX + doorW + 8, doorBottom + 6);
    ctx.lineTo(doorX + doorW + 8, bodyTop + 8);
    ctx.arc(cx, archCY + 8, archR + 8, 0, -Math.PI, true);
    ctx.fill();

    // ── 2. Door panel fill ──
    ctx.globalAlpha = 0.04;
    ctx.fillStyle = '#f5e6c8';
    ctx.beginPath();
    ctx.moveTo(doorX, bodyTop);
    ctx.lineTo(doorX, doorBottom);
    ctx.lineTo(doorX + doorW, doorBottom);
    ctx.lineTo(doorX + doorW, bodyTop);
    ctx.arc(cx, archCY, archR, 0, -Math.PI, true);
    ctx.fill();

    // ── 3. Full arch circle at top ──
    ctx.globalAlpha = 0.14;
    ctx.strokeStyle = col;
    ctx.lineWidth = 1.2;
    ctx.beginPath();
    ctx.arc(cx, archCY, archR, 0, Math.PI * 2);
    ctx.stroke();

    // ── 4. Door frame (gothic pointed arch + straight sides) ──
    ctx.globalAlpha = 0.25;
    ctx.strokeStyle = col;
    ctx.lineWidth = 2;
    ctx.lineJoin = 'round';
    ctx.beginPath();
    ctx.moveTo(doorX, doorBottom);
    ctx.lineTo(doorX, bodyTop);
    // Pointed arch: two big arcs meeting at the top
    ctx.arc(doorX + doorW, archCY, doorW, Math.PI, Math.PI * 0.77, true);
    ctx.arc(doorX, archCY, doorW, Math.PI * 0.23, 0, true);
    ctx.lineTo(doorX + doorW, doorBottom);
    ctx.stroke();

    // ── 5. Centre seam ──
    ctx.globalAlpha = 0.12;
    ctx.lineWidth = 0.8;
    ctx.beginPath();
    ctx.moveTo(cx, pageTop + 12);
    ctx.lineTo(cx, doorBottom);
    ctx.stroke();

    // ── 6. Cross braces ──
    ctx.globalAlpha = 0.07;
    ctx.lineWidth = 0.6;
    const braceTs = [0.25, 0.48, 0.70, 0.88];
    for (const t of braceTs) {
      const by = bodyTop + (doorBottom - bodyTop) * t;
      ctx.beginPath();
      ctx.moveTo(doorX + 6, by);
      ctx.lineTo(doorX + doorW - 6, by);
      ctx.stroke();
    }

    // ── 7. Title text at top of door (inside arch, no frame) ──
    const displayTitle = title || 'The Fold';
    const titleY = archCY - archR * 0.25;    // upper portion of the arch
    ctx.globalAlpha = 0.75;
    ctx.fillStyle = col;
    ctx.font = 'bold 28px "Palatino Linotype", "Book Antiqua", Palatino, Georgia, serif';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(displayTitle, cx, titleY);

    // Hand-drawn underline beneath title
    const lineW = Math.min(doorW * 0.55, 180);
    ctx.globalAlpha = 0.30;
    ctx.strokeStyle = col;
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(cx - lineW / 2, titleY + 20);
    ctx.bezierCurveTo(
      cx - lineW / 4, titleY + 22,
      cx + lineW / 4, titleY + 18,
      cx + lineW / 2, titleY + 20,
    );
    ctx.stroke();

    // ── 8. Bookmark emblem (centred at arch circle, bigger) ──
    const bmW = 56;
    const bmH = 190;
    const bmX = cx - bmW / 2;
    const bmY = archCY - bmH * 0.28;        // raised so it straddles the arch centre

    // Bookmark body
    ctx.globalAlpha = 0.04;
    ctx.fillStyle = '#d8c8a8';
    ctx.beginPath();
    ctx.moveTo(bmX, bmY);
    ctx.lineTo(bmX, bmY + bmH);
    ctx.lineTo(bmX + bmW / 2, bmY + bmH - 12);
    ctx.lineTo(bmX + bmW, bmY + bmH);
    ctx.lineTo(bmX + bmW, bmY);
    ctx.closePath();
    ctx.fill();

    // Bookmark border
    ctx.globalAlpha = 0.22;
    ctx.strokeStyle = col;
    ctx.lineWidth = 1.4;
    ctx.beginPath();
    ctx.moveTo(bmX, bmY);
    ctx.lineTo(bmX, bmY + bmH);
    ctx.lineTo(bmX + bmW / 2, bmY + bmH - 12);
    ctx.lineTo(bmX + bmW, bmY + bmH);
    ctx.lineTo(bmX + bmW, bmY);
    ctx.closePath();
    ctx.stroke();

    // Inner border (embellishment)
    ctx.globalAlpha = 0.10;
    ctx.lineWidth = 0.5;
    ctx.strokeRect(bmX + 5, bmY + 5, bmW - 10, bmH - 22);

    // Top/bottom decorative lines on bookmark
    ctx.globalAlpha = 0.15;
    ctx.lineWidth = 0.6;
    for (const ly of [bmY + 12, bmY + bmH - 22]) {
      ctx.beginPath();
      ctx.moveTo(bmX + 8, ly);
      ctx.lineTo(bmX + bmW - 8, ly);
      ctx.stroke();
    }

    // Four sigils: eye, key, lock, eye (top to bottom)
    const sigilCx = bmX + bmW / 2;
    const sigilZoneTop = bmY + 20;
    const sigilZoneH = bmH - 44;
    const sigilGap = sigilZoneH / 3;

    ctx.strokeStyle = col;
    ctx.fillStyle = col;

    this._drawSigilEye(ctx, sigilCx, sigilZoneTop, 0.22);
    this._drawSigilKey(ctx, sigilCx, sigilZoneTop + sigilGap, 0.22);
    this._drawSigilLock(ctx, sigilCx, sigilZoneTop + sigilGap * 2, 0.22);
    this._drawSigilEye(ctx, sigilCx, sigilZoneTop + sigilGap * 3, 0.22);

    // ── 9. Threshold stone ──
    ctx.globalAlpha = 0.12;
    ctx.strokeStyle = '#5a3820';
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(doorX - 16, doorBottom);
    ctx.lineTo(doorX + doorW + 16, doorBottom);
    ctx.stroke();

    // ── 10. Grass & overgrowth from the bottom ──
    this._drawGrass(ctx, doorX - 10, doorX + doorW + 10, doorBottom, col);

    ctx.restore();
  }

  // ── Sigil helpers ─────────────────────────────────────────────────────────

  /** Stylised eye sigil (almond shape + circle iris). */
  private _drawSigilEye(ctx: CanvasRenderingContext2D, cx: number, cy: number, alpha: number): void {
    const w = 10, h = 5;
    ctx.globalAlpha = alpha;
    ctx.lineWidth = 0.9;
    // Almond
    ctx.beginPath();
    ctx.moveTo(cx - w, cy);
    ctx.quadraticCurveTo(cx, cy - h, cx + w, cy);
    ctx.quadraticCurveTo(cx, cy + h, cx - w, cy);
    ctx.stroke();
    // Iris
    ctx.beginPath();
    ctx.arc(cx, cy, 2.5, 0, Math.PI * 2);
    ctx.fill();
    // Pupil dot
    ctx.globalAlpha = alpha * 1.2;
    ctx.beginPath();
    ctx.arc(cx, cy, 1, 0, Math.PI * 2);
    ctx.fill();
  }

  /** Stylised key sigil (circle bow + shaft + teeth). */
  private _drawSigilKey(ctx: CanvasRenderingContext2D, cx: number, cy: number, alpha: number): void {
    ctx.globalAlpha = alpha;
    ctx.lineWidth = 0.9;
    // Bow (circle at top)
    const bowR = 4;
    const bowY = cy - 6;
    ctx.beginPath();
    ctx.arc(cx, bowY, bowR, 0, Math.PI * 2);
    ctx.stroke();
    // Shaft
    const shaftTop = bowY + bowR;
    const shaftBot = cy + 8;
    ctx.beginPath();
    ctx.moveTo(cx, shaftTop);
    ctx.lineTo(cx, shaftBot);
    ctx.stroke();
    // Teeth (two small right-facing notches)
    for (const ty of [shaftBot - 3, shaftBot]) {
      ctx.beginPath();
      ctx.moveTo(cx, ty);
      ctx.lineTo(cx + 3.5, ty);
      ctx.lineTo(cx + 3.5, ty - 1.5);
      ctx.stroke();
    }
  }

  /** Stylised padlock sigil (body rectangle + shackle arc). */
  private _drawSigilLock(ctx: CanvasRenderingContext2D, cx: number, cy: number, alpha: number): void {
    ctx.globalAlpha = alpha;
    ctx.lineWidth = 0.9;
    const bw = 10, bh = 8;
    const bx = cx - bw / 2, by = cy - 1;
    // Body
    ctx.strokeRect(bx, by, bw, bh);
    // Shackle (arc above body)
    ctx.beginPath();
    ctx.arc(cx, by, bw * 0.35, Math.PI, 0, false);
    ctx.stroke();
    // Keyhole dot
    ctx.globalAlpha = alpha * 0.8;
    ctx.beginPath();
    ctx.arc(cx, by + bh * 0.42, 1.5, 0, Math.PI * 2);
    ctx.fill();
  }

  /** Grass tufts and vine overgrowth along a horizontal baseline. */
  private _drawGrass(ctx: CanvasRenderingContext2D, x0: number, x1: number, baseY: number, _col: string): void {
    ctx.strokeStyle = '#606060';
    ctx.fillStyle = '#505050';
    ctx.lineWidth = 0.7;
    ctx.lineCap = 'round';

    // Pseudo-random seeded by position (deterministic)
    const seed = (x0 * 7 + baseY * 13) | 0;
    const rng = (i: number) => {
      const v = Math.sin(seed + i * 127.1) * 43758.5453;
      return v - Math.floor(v);
    };

    const span = x1 - x0;
    const numBlades = Math.floor(span / 3);

    for (let i = 0; i < numBlades; i++) {
      const t = rng(i);
      const bx = x0 + t * span;
      const height = 12 + rng(i + 200) * 32;
      const lean = (rng(i + 400) - 0.5) * 16;
      const thick = rng(i + 600) > 0.6;

      ctx.globalAlpha = 0.06 + rng(i + 100) * 0.10;
      ctx.strokeStyle = `rgba(${70 + Math.floor(rng(i + 500) * 40)}, ${70 + Math.floor(rng(i + 500) * 40)}, ${70 + Math.floor(rng(i + 500) * 40)}, 1)`;
      ctx.lineWidth = thick ? 1.2 : 0.7;
      ctx.beginPath();
      ctx.moveTo(bx, baseY);
      ctx.quadraticCurveTo(bx + lean * 0.4, baseY - height * 0.6, bx + lean, baseY - height);
      ctx.stroke();

      // Occasional small leaf/dot at tip
      if (rng(i + 300) > 0.7) {
        ctx.globalAlpha = 0.08;
        ctx.fillStyle = '#707070';
        ctx.beginPath();
        ctx.arc(bx + lean, baseY - height, 1.5, 0, Math.PI * 2);
        ctx.fill();
      }
    }

    // A few vine tendrils climbing from corners
    ctx.strokeStyle = '#585858';
    for (const [vx, dir] of [[x0 + 8, 1], [x1 - 8, -1]] as [number, number][]) {
      ctx.globalAlpha = 0.07;
      ctx.lineWidth = 0.7;
      const vh = 30 + rng(777) * 40;
      ctx.beginPath();
      ctx.moveTo(vx, baseY);
      ctx.bezierCurveTo(
        vx + dir * 6, baseY - vh * 0.3,
        vx + dir * 2, baseY - vh * 0.7,
        vx + dir * 8, baseY - vh,
      );
      ctx.stroke();
      // Small leaves on vine
      for (let j = 0; j < 3; j++) {
        const lt = 0.3 + j * 0.25;
        const lx = vx + dir * (4 + j * 2);
        const ly = baseY - vh * lt;
        ctx.globalAlpha = 0.06;
        ctx.beginPath();
        ctx.ellipse(lx + dir * 3, ly, 2.5, 1.5, dir * 0.4, 0, Math.PI * 2);
        ctx.fill();
      }
    }

    ctx.lineCap = 'butt';
  }
}

function easeOutCubic(t: number): number {
  return 1 - Math.pow(1 - t, 3);
}
